/**
 * LCFPIMonetisation.js
 * Monetisation Drill-down Modal + PoA Carbon Workflow.
 * SSOT: all values from /api/lcfpi/monetisation/breakdown (same formula as PoA engine).
 */
import React, { useState, useEffect } from "react";
import client from "@/api/client";
import {
  X, Loader, DollarSign, CheckCircle2,
  AlertCircle, BarChart2, ChevronRight,
} from "lucide-react";

const fmtU = v => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};
const fmtN = v => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return `${n.toLocaleString()}`;
};

const TIER_COLOR = { "Tier 1": "#22c55e", "Tier 2": "#f59e0b", "Tier 3": "#ef4444" };

// ── Monetisation Drill-down Modal ────────────────────────────────────────────
export function MonetisationDrilldownModal({ onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/lcfpi/monetisation-breakdown")
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end"
      style={{ background: "#000000bb" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="h-full flex flex-col" data-testid="monetisation-drilldown-modal"
        style={{ width: "min(680px,100vw)", background: "#060d1a", borderLeft: "1px solid #1e293b" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 flex-shrink-0">
          <div>
            <p className="text-sm font-black text-white flex items-center gap-2">
              <DollarSign size={14} color="#fbbf24" />
              Monetisation Potential Breakdown
            </p>
            <p className="text-[10px] text-slate-500 mt-0.5">
              SSOT · Same source as PoA tab · $15/tCO₂e base scenario
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* Summary KPIs */}
        {data && (
          <div className="grid grid-cols-3 gap-3 px-6 pt-5 pb-3 flex-shrink-0">
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
              <p className="text-xl font-black text-yellow-400">{fmtU(data.total_revenue_yr)}/yr</p>
              <p className="text-xs text-slate-400 mt-0.5">Total Revenue Potential</p>
            </div>
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
              <p className="text-xl font-black text-green-400">{data.total_eligible ?? "—"}</p>
              <p className="text-xs text-slate-400 mt-0.5">Eligible Projects</p>
            </div>
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
              <p className="text-xl font-black text-blue-400">${data.carbon_price}/tCO₂e</p>
              <p className="text-xs text-slate-400 mt-0.5">Base Carbon Price</p>
            </div>
          </div>
        )}

        {/* Project table */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading && (
            <div className="flex justify-center py-12">
              <Loader size={18} className="animate-spin text-slate-500" />
            </div>
          )}

          {!loading && data?.projects?.length === 0 && (
            <div className="text-center py-12 text-slate-600">
              <BarChart2 size={28} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No assessed projects found</p>
            </div>
          )}

          {!loading && data?.projects && data.projects.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 mb-3">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Projects ({data.projects.length})
                </p>
              </div>

              {/* Table header */}
              <div className="grid gap-2 px-3 pb-2 border-b border-slate-800"
                style={{ gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr" }}>
                {["Project", "Sector / LGA", "tCO₂e", "Monetisable", "Revenue/yr"].map(h => (
                  <p key={h} className="text-[9px] font-bold text-slate-500 uppercase">{h}</p>
                ))}
              </div>

              {data.projects.map((p, i) => {
                const tc = TIER_COLOR[p.readiness_tier] || "#64748b";
                return (
                  <div key={p.project_id || i}
                    data-testid={`monet-project-row-${p.project_id}`}
                    className="grid gap-2 px-3 py-3 rounded-xl hover:bg-slate-800/60 transition-all"
                    style={{ gridTemplateColumns: "2fr 1.5fr 1fr 1fr 1fr", borderLeft: `2px solid ${tc}30` }}>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-white truncate">{p.org_name}</p>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                        style={{ background: `${tc}20`, color: tc, border: `1px solid ${tc}30` }}>
                        {p.readiness_tier || "—"}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-slate-300 capitalize truncate">{p.sector?.replace(/_/g, " ")}</p>
                      <p className="text-[10px] text-slate-500">{p.lga}</p>
                    </div>
                    <p className="text-xs font-bold text-slate-300 self-center">
                      {fmtN(p.total_tco2e)}t
                    </p>
                    <div className="self-center">
                      <p className="text-xs font-bold text-amber-400">{fmtN(p.monetisable_tco2e)}t</p>
                      <p className="text-[10px] text-slate-500">{p.methodology?.substring(0, 12)}</p>
                    </div>
                    <div className="self-center">
                      <p className="text-xs font-black text-yellow-400">{fmtU(p.base_revenue_yr)}</p>
                      <p className="text-[10px] text-slate-500">{p.registry?.substring(0, 9)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer caveat */}
        <div className="px-6 py-3 border-t border-slate-800 flex-shrink-0">
          <p className="text-[9px] text-slate-700 leading-relaxed">
            {data?.caveat || "INDICATIVE only — not bankable. Subject to VVB validation."}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── PoA Carbon Monetisation Workspace ────────────────────────────────────────
const POA_STEPS = [
  { id: "pdd",         label: "1. Project Design Document", color: "#3b82f6" },
  { id: "validation",  label: "2. Third-Party Validation",  color: "#f59e0b" },
  { id: "registration",label: "3. Registry Registration",   color: "#8b5cf6" },
  { id: "cpa",         label: "4. CPA Bundling",            color: "#0891b2" },
  { id: "monitoring",  label: "5. MRV / Monitoring",        color: "#34d399" },
  { id: "issuance",    label: "6. Credit Issuance",         color: "#22c55e" },
];

function computeRouteEligibility(projects = []) {
  const assessed   = (projects || []).filter(p => (p.readiness_score || 0) > 0);
  const tier1      = assessed.filter(p => p.readiness_tier === "Tier 1");
  const tier2      = assessed.filter(p => p.readiness_tier === "Tier 2");
  const eligibleCPAs = tier1.length + tier2.length;
  const hasWaste   = assessed.some(p => ["waste_management","manufacturing"].includes(p.sector));
  const hasPR      = assessed.some(p => p.verification_status === "programme_reviewed");
  const totalTco2e = assessed.reduce((s, p) => s + Number(p.total_tco2e || 0), 0);

  return [
    {
      id: "vcm", label: "Voluntary Carbon Market (VCM)", sub: "Verra VCS · Gold Standard · GS4GG",
      status: tier1.length > 0 ? "eligible" : tier2.length > 0 ? "potentially_eligible" : "not_eligible",
      score: tier1.length > 0 ? 78 : tier2.length > 0 ? 48 : 18,
      reason: tier1.length > 0
        ? `${tier1.length} Tier 1 project${tier1.length > 1 ? "s" : ""} meet VCM baseline criteria`
        : tier2.length > 0
          ? `${tier2.length} project${tier2.length > 1 ? "s" : ""} require readiness improvement to Tier 1`
          : "Pipeline does not yet meet VCM baseline criteria — assess projects first",
      missing: tier1.length > 0
        ? ["VVB third-party validation (accredited DOE)", "Monitoring & Reporting Plan (MRP)", "Registry selection and account setup"]
        : ["Tier 1 readiness (score ≥ 65)", "VVB engagement", "Baseline study completion"],
      next_action: tier1.length > 0
        ? `Engage accredited VVB for ${tier1.length} Tier 1 project${tier1.length > 1 ? "s" : ""}`
        : "Improve readiness scores to Tier 1 before VCM engagement",
    },
    {
      id: "article_6_2", label: "Article 6.2 — Bilateral ITMOs", sub: "Country-to-country transfer of mitigation outcomes",
      status: "potentially_eligible", score: 32,
      reason: "Nigeria is developing its Article 6 framework. Pre-authorization possible for Tier 1 projects once NDC alignment confirmed.",
      missing: [
        "Host country (Nigeria) written authorization letter",
        "Bilateral transfer agreement with purchasing country",
        "Corresponding adjustment framework at national level",
        "NDC sector boundary compatibility confirmation",
      ],
      next_action: "Engage Nigeria NCCC / Ministry of Environment for Article 6.2 pipeline authorization",
    },
    {
      id: "article_6_4", label: "Article 6.4 — UN Supervised Mechanism", sub: "UNFCCC Supervisory Body · multilateral",
      status: "not_eligible", score: 12,
      reason: "Article 6.4 mechanism is not yet fully operational. Methodologies and validation pathways under UNFCCC development (2026–2027).",
      missing: [
        "Approved methodology under UNFCCC Supervisory Body",
        "Mechanism registration (not yet open for submissions)",
        "Validation pathway under new Article 6.4 rules",
        "Host country written approval per Article 6.4 process",
      ],
      next_action: "Monitor UNFCCC Supervisory Body decisions. Expected operational readiness for most activity types: 2026–2027.",
    },
    {
      id: "poa_cpa", label: "Programme of Activities / CPA Route", sub: "CDM PoA · Verra VCS PoA · Gold Standard PoA",
      status: eligibleCPAs >= 3 ? "eligible" : "potentially_eligible",
      score: eligibleCPAs >= 5 ? 82 : eligibleCPAs >= 3 ? 65 : 42,
      reason: eligibleCPAs >= 5
        ? `${eligibleCPAs} eligible CPAs identified — sufficient for a bankable PoA programme`
        : eligibleCPAs >= 3
          ? `${eligibleCPAs} CPAs identified — minimum viable. 5+ recommended for bankability`
          : `${eligibleCPAs} CPAs identified — expand pipeline to at least 5 CPAs for PoA viability`,
      missing: [
        "CPA inclusion documentation per project",
        "Shared monitoring structure agreement (PoA Coordinating Entity)",
        "Programme Design Document (PoA-PDD)",
        eligibleCPAs < 5 ? `${5 - eligibleCPAs} additional CPA${5 - eligibleCPAs > 1 ? "s" : ""} needed` : null,
      ].filter(Boolean),
      next_action: eligibleCPAs >= 5
        ? "Proceed to PoA-PDD drafting with accredited DOE"
        : `Add ${Math.max(0, 5 - eligibleCPAs)} more CPA${5 - eligibleCPAs > 1 ? "s" : ""} to reach bankable PoA threshold`,
    },
    {
      id: "plastic", label: "Plastic / Circularity Credits", sub: "Verra Plastic Waste Reduction Standard · PCR",
      status: hasWaste ? "potentially_eligible" : "not_eligible",
      score: hasWaste ? 52 : 8,
      reason: hasWaste
        ? "Waste and circularity projects in pipeline may qualify for plastic credit issuance under PCR or Verra standards"
        : "No waste management or circularity projects in assessed pipeline — plastic credit route unavailable",
      missing: hasWaste
        ? ["Chain of custody documentation (audited)", "Third-party verification of recovery volumes", "PCR / Verra plastic standard registration", "Material type and quality certification"]
        : ["Waste management or recycling projects in pipeline"],
      next_action: hasWaste
        ? "Engage Verra or South Pole for plastic credit pre-feasibility"
        : "Add waste/circularity projects to pipeline to unlock this route",
    },
  ];
}

const STATUS_META = {
  eligible:             { label: "Eligible",             color: "#22c55e", bg: "#14532d20" },
  potentially_eligible: { label: "Potentially Eligible", color: "#f59e0b", bg: "#78350f20" },
  not_eligible:         { label: "Not Yet Eligible",     color: "#ef4444", bg: "#7f1d1d20" },
};

export function PoACarbonWorkspace({ poa, projects = [] }) {
  const [activePanel, setActivePanel] = useState("eligible");
  const [expandedRoute, setExpandedRoute] = useState(null);

  const eligible = (projects || []).filter(p =>
    (p.readiness_score || 0) > 0 && (p.total_tco2e || 0) > 0
  );

  const totalRevenue = eligible.reduce((sum, p) =>
    sum + (Number(p.total_tco2e || 0) * 0.35 * 0.35 * 15), 0
  );

  const routeEligibility = computeRouteEligibility(projects);

  const panels = [
    { id: "eligible", label: "Eligible Projects" },
    { id: "route",    label: "Monetisation Route" },
    { id: "roadmap",  label: "Process Roadmap" },
    { id: "revenue",  label: "Revenue Breakdown" },
    { id: "actions",  label: "Next Actions" },
  ];

  return (
    <div className="mt-5" data-testid="poa-carbon-workspace">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold text-yellow-400 uppercase tracking-wider">
          Carbon Monetisation via PoA
        </p>
        <p className="text-[10px] text-slate-600">INDICATIVE · Subject to VVB validation</p>
      </div>

      {/* Panel tabs */}
      <div className="flex gap-1 mb-4 bg-slate-800/40 rounded-xl p-1 w-fit overflow-x-auto">
        {panels.map(panel => (
          <button key={panel.id} onClick={() => setActivePanel(panel.id)}
            data-testid={`poa-panel-${panel.id}`}
            className={`text-[10px] px-3 py-1.5 rounded-lg font-semibold whitespace-nowrap transition-all
              ${activePanel === panel.id ? "bg-slate-600 text-white" : "text-slate-500 hover:text-slate-300"}`}>
            {panel.label}
          </button>
        ))}
      </div>

      {/* Eligible Projects */}
      {activePanel === "eligible" && (
        <div className="space-y-1.5">
          {eligible.length === 0 && (
            <div className="text-center py-6 text-slate-600">
              <AlertCircle size={20} className="mx-auto mb-2 opacity-30" />
              <p className="text-xs">No assessed projects — run assessments first</p>
            </div>
          )}
          {eligible.slice(0, 8).map(p => {
            const tco2e = Number(p.total_tco2e || 0);
            const rev   = tco2e * 0.35 * 0.35 * 15;
            const tc    = TIER_COLOR[p.readiness_tier] || "#64748b";
            return (
              <div key={p.project_id}
                className="flex items-center gap-4 bg-slate-800/40 border border-slate-700/30 rounded-xl px-4 py-3"
                style={{ borderLeft: `3px solid ${tc}` }}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{p.org_name}</p>
                  <p className="text-xs text-slate-500">{p.sector?.replace(/_/g, " ")} · {p.lga}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-black text-green-400">{fmtN(tco2e)} tCO₂e</p>
                  <p className="text-[10px] text-slate-500">baseline</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-black text-yellow-400">{fmtU(rev)}/yr</p>
                  <p className="text-[10px] text-slate-500">est. revenue</p>
                </div>
              </div>
            );
          })}
          {eligible.length > 8 && (
            <p className="text-[10px] text-slate-600 text-center pt-1">+{eligible.length - 8} more eligible projects</p>
          )}
        </div>
      )}

      {/* Monetisation Route — dynamic eligibility */}
      {activePanel === "route" && (
        <div className="space-y-2">
          {routeEligibility.map(route => {
            const meta      = STATUS_META[route.status] || STATUS_META.not_eligible;
            const isExpanded = expandedRoute === route.id;
            const scorePct  = Math.min(route.score, 100);
            return (
              <div key={route.id}
                data-testid={`poa-route-${route.id}`}
                className="rounded-xl border overflow-hidden transition-all"
                style={{ border: `1px solid ${isExpanded ? meta.color + "50" : "#1e293b"}`, background: "#0d1726" }}>
                {/* Route header */}
                <button
                  onClick={() => setExpandedRoute(isExpanded ? null : route.id)}
                  className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-slate-800/50 transition-all">
                  {/* Score bar */}
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}30` }}>
                    <p className="text-xs font-black" style={{ color: meta.color }}>{route.score}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p className="text-xs font-bold text-white">{route.label}</p>
                      <span className="text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.color}30` }}>
                        {meta.label}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500">{route.sub}</p>
                  </div>
                  <ChevronRight size={12} className="text-slate-600 flex-shrink-0 transition-transform"
                    style={{ transform: isExpanded ? "rotate(90deg)" : "none" }} />
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-slate-800/60 pt-3 space-y-3">
                    {/* Eligibility bar */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[10px] text-slate-500">Eligibility Score</p>
                        <p className="text-[10px] font-bold" style={{ color: meta.color }}>{route.score}/100</p>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-800">
                        <div className="h-1.5 rounded-full transition-all"
                          style={{ width: `${scorePct}%`, background: meta.color }} />
                      </div>
                    </div>

                    {/* Reason */}
                    <div className="bg-slate-800/40 rounded-lg px-3 py-2">
                      <p className="text-[10px] text-slate-300 leading-relaxed">{route.reason}</p>
                    </div>

                    {/* Missing requirements */}
                    {route.missing?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider mb-1.5">
                          Missing Requirements
                        </p>
                        <div className="space-y-1">
                          {route.missing.map((req, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <AlertCircle size={9} className="text-amber-500 flex-shrink-0 mt-0.5" />
                              <p className="text-[10px] text-slate-400 leading-relaxed">{req}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Next action */}
                    <div className="bg-blue-900/15 border border-blue-700/25 rounded-lg px-3 py-2">
                      <p className="text-[9px] font-bold text-blue-400 uppercase tracking-wider mb-0.5">
                        Next Action
                      </p>
                      <p className="text-[10px] text-blue-200/90 leading-relaxed">{route.next_action}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <p className="text-[9px] text-slate-700 text-center pt-1">
            Eligibility scores are indicative · Based on portfolio readiness, verification status, and route criteria
          </p>
        </div>
      )}

      {/* Process Roadmap */}
      {activePanel === "roadmap" && (
        <div className="space-y-2">
          {POA_STEPS.map((step, i) => (
            <div key={step.id} data-testid={`poa-step-${step.id}`}
              className="flex items-center gap-4 rounded-xl px-4 py-3"
              style={{ background: "#0d1726", border: `1px solid ${i === 0 ? step.color + "40" : "#1e293b"}` }}>
              <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: i === 0 ? step.color : "#1e293b", border: `1px solid ${i === 0 ? step.color : "#334155"}` }}>
                {i === 0 ? <CheckCircle2 size={11} color="#fff" /> :
                  <span style={{ fontSize: 9, fontWeight: 700, color: "#64748b" }}>{i + 1}</span>}
              </div>
              <p className="text-xs font-bold text-white flex-1">{step.label}</p>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                style={{
                  background: i === 0 ? `${step.color}20` : "#1e293b",
                  color: i === 0 ? step.color : "#475569",
                }}>
                {i === 0 ? "In Progress" : "Pending"}
              </span>
            </div>
          ))}
          <p className="text-[10px] text-slate-700 text-center pt-1">
            Timeline to first credit issuance: 18–36 months from PDD completion
          </p>
        </div>
      )}

      {/* Revenue Breakdown */}
      {activePanel === "revenue" && (
        <div>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
              <p className="text-xl font-black text-yellow-400">{fmtU(totalRevenue)}/yr</p>
              <p className="text-xs text-slate-400 mt-0.5">Estimated Revenue</p>
            </div>
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
              <p className="text-xl font-black text-green-400">{eligible.length}</p>
              <p className="text-xs text-slate-400 mt-0.5">Eligible Projects</p>
            </div>
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
              <p className="text-xl font-black text-blue-400">$15/t</p>
              <p className="text-xs text-slate-400 mt-0.5">Base Carbon Price</p>
            </div>
          </div>
          <div className="space-y-2">
            {eligible.slice(0, 6).map(p => {
              const rev = Number(p.total_tco2e || 0) * 0.35 * 0.35 * 15;
              const pct = totalRevenue > 0 ? (rev / totalRevenue * 100) : 0;
              return (
                <div key={p.project_id} className="flex items-center gap-3">
                  <span className="text-[10px] text-slate-400 truncate" style={{ width: 120 }}>{p.org_name}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-800">
                    <div className="h-2 rounded-full"
                      style={{ width: `${pct}%`, background: TIER_COLOR[p.readiness_tier] || "#fbbf24", transition: "width .3s" }} />
                  </div>
                  <span className="text-[10px] font-bold text-yellow-400" style={{ width: 52, textAlign: "right" }}>
                    {fmtU(rev)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Next Actions */}
      {activePanel === "actions" && (
        <div className="space-y-2">
          {[
            { p: "P0", text: "Engage accredited DOE for PDD pre-screening",                          color: "#ef4444" },
            { p: "P0", text: "Complete baseline emissions measurement (Tier 2 evidence)",             color: "#ef4444" },
            { p: "P1", text: "Select and register PoA methodology with Verra VCS or Gold Standard",  color: "#f59e0b" },
            { p: "P1", text: `Bundle ${eligible.length} eligible CPAs under shared PoA umbrella`,    color: "#f59e0b" },
            { p: "P2", text: "Set up MRV system for continuous credit issuance post-registration",   color: "#22c55e" },
            { p: "P2", text: "Identify offtake partner / carbon buyer for forward sale agreement",   color: "#22c55e" },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 bg-slate-800/40 border border-slate-700/30 rounded-xl px-4 py-3">
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5"
                style={{ background: `${item.color}20`, color: item.color, border: `1px solid ${item.color}30` }}>
                {item.p}
              </span>
              <p className="text-xs text-slate-300 leading-relaxed">{item.text}</p>
            </div>
          ))}
          <p className="text-[10px] text-slate-700 text-center pt-1">
            Engage a carbon project developer / DOE for formal feasibility assessment.
          </p>
        </div>
      )}
    </div>
  );
}
